import { Heading } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { useEffect } from 'react'
import { VerifyChallenge } from '../../../src/component'
import { Route, usePostLoginRoute } from '../../../src/config/router'
import { TrackPageViewed } from '../../../src/integrations/monitoring'
import { CheckoutPage, TerminalErrorCode } from '../../../src/model'
import { accountLookup, setCheckoutError, useAppDispatch, useAppRouter, useMobileLogin, useIsApplePaylater } from '../../../src/state'
import { useCheckoutSizes } from '../../../src/utils/responsive'
import { LoginHelpLink } from '../../../src/component/LoginHelpLink'
import { PageNames } from '../../../src/model/monitoring'
import { markUsFebConsentInteractiveLogin } from '../../flow/one-offer-page/consentLoginSignal'

export const VerifyChallengePage: CheckoutPage = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const router = useAppRouter()
  const { headingSize } = useCheckoutSizes()
  const { dateOfBirthVerificationRequired, verifiedRoute } = useMobileLogin()
  // Apple Pay Later never routes to CREATE_PASSWORD.
  const skipPasswordCreation = useIsApplePaylater()
  const postLoginRoute = usePostLoginRoute()
  const onError = (isMobileLoginVerification: boolean): void => {
    dispatch(
      setCheckoutError({
        error: isMobileLoginVerification ? TerminalErrorCode.MaxDobVerificationAttempts : TerminalErrorCode.MaxAttempts,
      })
    )
    router.push(Route.TERMINAL_ERROR)
  }

  const onSuccess = async (): Promise<void> => {
    // A challenge that completes a login is deliberately not a remember-me mint site, but it IS a
    // fresh interactive login for the US FEB consent POST. The CREATE_PASSWORD branch is not
    // marked here — CreatePasswordPage marks on its own completion.
    if (dateOfBirthVerificationRequired && verifiedRoute !== undefined) {
      if (verifiedRoute === Route.LOGGED_IN) {
        markUsFebConsentInteractiveLogin()
      }
      router.push(verifiedRoute)
    } else if (skipPasswordCreation) {
      try {
        const account = unwrapResult(await dispatch(accountLookup()))
        // Same LOGGED_IN gate and SIGN_UP-detour convergence as Password.tsx.
        if (postLoginRoute === Route.LOGGED_IN) {
          markUsFebConsentInteractiveLogin()
        }
        if (!account.profileConfirmed) {
          router.push(Route.SIGN_UP)
        } else {
          router.push(postLoginRoute, account.preferredLocale)
        }
      } catch {
        dispatch(setCheckoutError({ error: TerminalErrorCode.Default }))
        router.push(Route.TERMINAL_ERROR)
      }
    } else {
      router.push(Route.CREATE_PASSWORD)
    }
  }

  useEffect(() => {
    router.prefetch(Route.TERMINAL_ERROR)
    if (skipPasswordCreation) {
      router.prefetch(postLoginRoute)
      router.prefetch(Route.SIGN_UP)
    } else {
      router.prefetch(Route.CREATE_PASSWORD)
    }
    router.prefetch(Route.ENTER_PASSWORD)
  }, [skipPasswordCreation, postLoginRoute])

  return (
    <>
      <Heading size={headingSize}>{t('verifyChallenge:heading')}</Heading>
      <VerifyChallenge onSuccess={onSuccess} onError={onError} isMobileLoginVerification={dateOfBirthVerificationRequired} />
      <LoginHelpLink pageName={PageNames.DOB_LAST_NAME_VERIFY} />
      <TrackPageViewed pageName={dateOfBirthVerificationRequired ? PageNames.DOB_LAST_NAME_VERIFY : undefined} />
    </>
  )
}
